import { memo } from 'react';
import { computeMovementArrowLayout, projectFrameInBox } from './shotMovementArrows';
import styles from './ShotMovementOverlay.module.css';

export interface ShotMovementOverlayProps {
  /** NATIVE movement enum name ('DollyInShot'). Board surfaces hold the web label and convert with
   *  shotMovementToNative; the Shot Info sheet's dropdown value is already native. */
  nativeMovement?: string;
  /** The project's aspect ratio as a number (parseAspectRatio of project.aspectRatio) — the same
   *  value Unity feeds SetShotMovementType. Handed down rather than read from the store: the board
   *  mounts one of these per tile, and ShotActionsPanel/ShotSettingsModal keep store-free test
   *  harnesses. Falls back to 16:9, which is also the loader's default. */
  aspectRatio?: number;
  /** Aspect of the BOX this overlay fills. Of the six call sites, FOUR sit in a fixed 16:9 box and
   *  take this default (see each one's CSS): the board grid tile (.thumb, SceneBoardExpanded), the
   *  Shot Info header thumb (ShotSettingsModal), the shot panel's preview frame (.thumb,
   *  ShotActionsPanel) and that preview's fullscreen magnification (.mediaBox,
   *  ShotPreviewFullscreen). The other two pass their own because their box tracks the project
   *  aspect: the Timeline shot lane (ShotBar's .movementFrame) and the viewer's contentRect
   *  (ViewerOverlay). Pass it if a surface ever stops being 16:9 — and note the trap the fullscreen
   *  preview hit on 2026-08-28: a box that sets BOTH width and height (or a height cap next to
   *  `width: 100%`) has its `aspect-ratio` ignored, so it silently stops being 16:9 while its CSS
   *  still says it is, and the arrows lay out against a box the image does not fill. */
  containerAspect?: number;
}

/**
 * Web port of Unity's ShotMovementArrowsOverlayController — the camera-movement arrows composited
 * over a shot image. Purely decorative: the movement is already named in text beside every surface
 * that mounts this, so it is aria-hidden and adds no i18n key.
 *
 * memo'd on its three scalar props because the board is unvirtualized and unmemoized: a tile
 * re-renders on every board pass, and only a movement or aspect-ratio change can alter this subtree.
 */
export const ShotMovementOverlay = memo(function ShotMovementOverlay(
  { nativeMovement, aspectRatio, containerAspect = 16 / 9 }: ShotMovementOverlayProps,
) {
  const ar = aspectRatio && aspectRatio > 0 ? aspectRatio : 16 / 9;
  const arrows = computeMovementArrowLayout(nativeMovement, ar);
  if (arrows.length === 0) return null;

  // The image is object-fit:contain inside a box of a FIXED aspect, so a project at any other
  // aspect letterboxes. Size the arrow frame to the image's real rect — Unity's container is the
  // shot rect, not the widget — or the arrows would sit against the matte instead of the frame.
  // 20260930 - the math is shared with ShotPreviewFullscreen's still frame (projectFrameInBox), so
  // the fullscreen crop and these arrows always agree on where the shot frame is.
  const frame = projectFrameInBox(ar, containerAspect);

  return (
    <span className={styles.layer} aria-hidden="true" data-testid="shot-movement-overlay">
      <span className={styles.frame} style={frame}>
        {arrows.map((a, i) => (
          <span
            key={i}
            className={styles.arrow}
            data-testid="shot-movement-arrow"
            style={{
              left: `${a.leftPct}%`,
              top: `${a.topPct}%`,
              width: `${a.widthPct}%`,
              height: `${a.heightPct}%`,
              transform: a.transform,
              ['--arw' as string]: `url(${a.sprite})`,
            }}
          />
        ))}
      </span>
    </span>
  );
});
